import Button from '@/components/Buttons/Button';
import Dialog from '@/components/Dialog/BootstrapDialog';
import CheckCircleOrange from '@/images/CheckCircleOrange.svg';
import DangerCircle from '@/images/DangerCircle.svg';
import LoadingAnimation from '@/images/LoadingAnimation.json';
import SmallCloseIcon from '@/images/SmallCloseIcon.svg';
import ActionDialog from '@/pages/account-settings/components/AccountActions/ActionDialog';
import { cancelationProcessFlag } from '@/pages/account-settings/components/Billing';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { useFlag } from '@/services/featureFlags';
import { creditsToVideoMinutes } from '@/state/credits/credits';
import { PaymentProvider } from '@/state/user-settings/types';
import { StyleCollection } from '@/utils/types';
import styled from '@emotion/styled';
import { Player } from '@lottiefiles/react-lottie-player';
import { DialogActions, DialogContent, DialogTitle, Link, Typography } from '@mui/material';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import React, { useEffect, useState } from 'react';
import { useCancelSubscription } from './useCancelSubscription';
import { useUpdateSubscriptionAfterCancel } from './useUpdateSubscriptionAfterCancel';

const CancelDialog = styled.div`
    text-align: center;
`;

const DiscountDiv = styled.div`
    display: flex;
    padding: 24px;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    border-radius: 12px;
    background: ${({ theme }) => theme.colors.primary.bgLight};
    align-items: center;
    gap: 4px;
`;

const DiscountTitleTypography = styled(Typography)`
    color: ${({ theme }) => theme.colors.black[600]};
    font-size: 14px;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.28px;
`;

const DiscountTypography = styled(Typography)`
    color: ${({ theme }) => theme.colors.primary.main};
    font-size: 24px;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.48px;
    margin-bottom: 12px;
`;

const SubTitle = styled(Typography)`
    color: ${({ theme }) => theme.colors.black.main};
    line-height: 140%;
    letter-spacing: 0.15px;
    margin-top: 6px;
    margin-bottom: 16px;
`;

const Loading = styled(Player)`
    height: 25rem;
    width: 70%;
`;

const StyledDialogContent = styled(DialogContent)`
    justify-content: center;
    align-items: center;
`;

const ConfirmationDiv = styled.div`
    height: 289px;
    align-items: center;
    display: flex;
    justify-content: center;
    flex-direction: column;
`;

const CreditsAlert = styled.div`
    display: flex;
    flex-direction: row;
    width: 445px;
    height: 86px;
    border-radius: 12px;
    padding: 24px;
    gap: 10px;
    background-color: ${({ theme }) => theme.colors.black[100]};
    margin: 5px 0px 15px 0px;
    align-items: center;
`;

const ContactContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 5px;
    height: 19px;
`;

const DangerCircleImg = styled.img`
    width: 24px;
    height: 24px;
`;

const CloseIcon = styled.img`
    align-self: end;
`;

const CreditsAlertText = styled(Typography)`
    color: ${({ theme }) => theme.colors.secondary[700]};
    font: 'Libre Franklin';
    font-size: 16px;
    white-space: pre-line;
`;

const ContactText = styled(Typography)`
    color: ${({ theme }) => theme.colors.secondary.main};
    font: 'Libre Franklin';
    font-size: 16px;
`;

const ContactEmail = styled(Link)`
    color: #497cff;
    font: 'Libre Franklin';
    font-size: 16px;
    text-decoration: none;
    &:hover {
        cursor: pointer;
        text-decoration: underline;
    }
`;

const Title = styled(DialogTitle)`
    color: ${({ theme }) => theme.colors.black.main};
    margin-bottom: 16px;
    padding: 0px !important;
    font-size: 24px !important;
`;

const styles: StyleCollection = {
    closeIcon: theme => ({
        marginLeft: 'auto',
        color: theme.colors.primary,
        margin: `30px 10px 0 auto`,
    }),
    closeButton: () => ({
        width: '192px',
        fontSize: '1.6rem',
    }),
    submitButton: () => ({
        width: '192px',
        fontSize: '1.6rem',
        marginLeft: '64px !important',
    }),
    dialog: {
        width: '508px',
        padding: '30px',
        paddingTop: '24px',
        paddingBottom: '24px',
        alignItems: 'center',
        textAlign: 'center',
        fontWeight: 700,
        letterSpacing: '-0.48px',
        borderRadius: '20px',
    },
    dialogContent: {
        padding: '0px',
        marginBottom: '32px',
        fontSize: '16px',
        fontWeight: '400',
        lineHeight: '140%',
        letterSpacing: '0.15px',
    },
    contentStyle: {
        flex: 1,
        overflow: 'visible',
        alignItems: 'center',
        width: '53rem',
        height: '32rem',
        display: 'flex',
    },
};

const ActionButton = styled(Button, { shouldForwardProp: p => !['hidden', 'disabled'].includes(p) })<{
    disabled: boolean;
}>`
    padding: 10px 16px 10px 16px;
    font-size: 1.6rem;
    border-radius: 8px;
    line-height: 1;
    width: 192px;
    height: 40px;
    min-height: 40px;
    display: ${({ hidden }) => (hidden ? 'none' : 'flex')};
    gap: 6px;
`;

const ContinueActionButton = styled(ActionButton)`
    width: 230px;
    margin-top: 16px;
`;

const ConfirmationTitle = styled(Typography)`
    color: ${({ theme }) => theme.colors.secondary.main};
    text-align: center;
    font-size: 28px;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.56px;
    margin-top: 24px;
`;

const ConfirmationSubTitle = styled(Typography)`
    color: ${({ theme }) => theme.colors.secondary[700]};
    text-align: center;
    line-height: normal;
    letter-spacing: 0.15px;
`;

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const monthlyCancelCoupon = {
    id: 'exYy9eWf',
    name: '5043AI2 | 50% off 3 months',
    promoCode: '',
};

const annualCancelCoupon = {
    id: 'ixxTvbgg',
    name: '1041AI | 10% off for 1 year',
    promoCode: '',
};

interface Props {
    isOpen: boolean;
    setIsOpen: any;
    setOpenMobileModal: any;
    cancelationFlag: cancelationProcessFlag;
    isSuggestDiscount: boolean;
    credits?: any;
}

export const CancelModal = ({ isOpen, setIsOpen, setOpenMobileModal, cancelationFlag, isSuggestDiscount, credits }: Props) => {
    const { user } = useAuth();
    const mixpanel = useAnalytics();
    const [isLoadingOpen, setIsLoadingOpen] = useState(false);
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const [isCancelationOpen, setIsCancelationOpen] = useState(false);
    const { updateSubscription, isUpdating } = useUpdateSubscriptionAfterCancel({
        setOpen: setIsOpen,
        setIsLoadingOpen,
        setIsConfirmationOpen,
    });
    const { cancelSubscription, isCanceling } = useCancelSubscription({
        setOpen: setIsOpen,
        setIsLoadingOpen,
        setIsCancelationOpen,
    });
    const creditsToMinutesEnabled = useFlag('credits_to_minutes_enabled', false);

    useEffect(() => {
        setIsLoadingOpen(isUpdating);
    }, [isUpdating]);

    useEffect(() => {
        setIsLoadingOpen(isCanceling);
    }, [isCanceling]);

    const discountText = () => {
        return user?.billingInterval === 'month' ? '50% off for 3 months' : '10% off Annual Plan';
    };

    const discountCoupon = () => {
        return user?.billingInterval === 'month' ? monthlyCancelCoupon : annualCancelCoupon;
    };

    const applyCoupon = async () => {
        if (user?.paymentProvider === PaymentProvider.AppStore) {
            mixpanel?.track('goto-mobile-manage-account', {
                redirect: 'root',
                cause: 'mobile user',
            });
            setOpenMobileModal(true);
        } else {
            mixpanel?.track('cancel_promotion_popup', {
                event: 'accept',
                currentPlan: user?.planGroup,
                planType: user?.billingInterval,
            });
            await updateSubscription({ priceId: user?.priceId!, coupon: discountCoupon().id });
        }
    };

    const applyCancelSubscription = async (from: string) => {
        if (user?.paymentProvider === PaymentProvider.AppStore) {
            mixpanel?.track('goto-mobile-manage-account', {
                redirect: 'root',
                cause: 'mobile user',
            });
            setOpenMobileModal(true);
        } else {
            mixpanel?.track('cancel-subscription', {
                from: from,
                event: 'cancel',
            });
            await cancelSubscription();
        }
    };

    const onCancelDialog = async (from: string) => {
        mixpanel?.track('cancel-subscription', {
            from: from,
            event: 'close',
        });
        setIsOpen(false);
    };

    const dialogContent = () => {
        return (
            <CancelDialog>
                <SubTitle>We have a few great product updates coming up and we'd love for you to stick around.</SubTitle>
                <DiscountDiv>
                    <DiscountTitleTypography>Claim your limited time offer</DiscountTitleTypography>
                    <DiscountTypography>{discountText()}</DiscountTypography>
                    <ActionButton disabled={false} onClick={applyCoupon}>
                        Accept offer
                    </ActionButton>
                </DiscountDiv>
            </CancelDialog>
        );
    };

    const ConfirmationModal = () => {
        return (
            <Dialog open={isConfirmationOpen} width="50rem" rootstyle={styles.dialog} contentstyle={styles.dialogContent}>
                <StyledDialogContent>
                    <CloseIcon src={SmallCloseIcon} onClick={() => setIsConfirmationOpen(false)} />
                    <ConfirmationDiv>
                        <img src={CheckCircleOrange} />
                        <ConfirmationTitle>Your plan has been updated</ConfirmationTitle>
                        <ConfirmationSubTitle>The offer will be applied on your next billing cycle</ConfirmationSubTitle>
                    </ConfirmationDiv>
                    <ContinueActionButton
                        disabled={false}
                        onClick={() => {
                            setIsConfirmationOpen(false);
                            setIsOpen(false);
                        }}
                        color={'secondary'}>
                        Continue
                    </ContinueActionButton>
                </StyledDialogContent>
            </Dialog>
        );
    };

    const CancelationModal = () => {
        return (
            <Dialog open={isCancelationOpen} width="50rem" rootstyle={styles.dialog} contentstyle={styles.dialogContent}>
                <StyledDialogContent>
                    <CloseIcon src={SmallCloseIcon} onClick={() => setIsCancelationOpen(false)} />
                    <ConfirmationDiv>
                        <img src={CheckCircleOrange} />
                        <ConfirmationTitle>Your plan has been canceled</ConfirmationTitle>
                    </ConfirmationDiv>
                    <ContinueActionButton
                        disabled={false}
                        onClick={() => {
                            setIsCancelationOpen(false);
                            setIsOpen(false);
                        }}
                        color={'secondary'}>
                        Continue
                    </ContinueActionButton>
                </StyledDialogContent>
            </Dialog>
        );
    };

    const LoadingModal = () => {
        return (
            <Dialog open={isLoadingOpen} width="50rem" rootstyle={styles.dialog} contentstyle={styles.dialogContent}>
                <StyledDialogContent>
                    <Loading src={LoadingAnimation} autoplay loop />
                </StyledDialogContent>
            </Dialog>
        );
    };

    const rollOverDialogContent = () => {
        return (
            <>
                <CreditsAlert>
                    <DangerCircleImg src={DangerCircle} />
                    <CreditsAlertText variant="h4" fontWeight={500}>
                        {`Unused ${creditsToMinutesEnabled ? 'minutes' : 'credits'} will be forfeited at the end of \n your last billing cycle. This action is irreversible.`}
                    </CreditsAlertText>
                </CreditsAlert>
                <ContactContainer>
                    <ContactText fontWeight={500}>{'Have questions or issues? contact '}</ContactText>
                    <ContactEmail href="mailto: support@d-id.com" fontWeight={500}>
                        support@d-id.com
                    </ContactEmail>
                </ContactContainer>
            </>
        );
    };

    const rollOverDialog = () => {
        return (
            <ActionDialog
                open={isOpen}
                onClose={() => setIsOpen(false)}
                onCancel={() => onCancelDialog('rollover popup')}
                title={
                    creditsToMinutesEnabled
                        ? `Are you sure you want to go? \n You will lose your remaining ${creditsToVideoMinutes(credits?.remaining)} minutes`
                        : `Are you sure you want to go? \n You will lose your remaining ${credits?.remaining} credits`
                }
                subtitle={rollOverDialogContent()}
                onSubmit={() => applyCancelSubscription('rollover popup')}
                submitTitle="Yes, continue"
                cancelTitle="Don't cancel"
                warningIcon={false}
            />
        );
    };

    const discountDialog = () => {
        return (
            <ActionDialog
                open={isOpen}
                onClose={() => setIsOpen(false)}
                onCancel={() => onCancelDialog('promotion popup')}
                title={`Before you go...`}
                subtitle={dialogContent()}
                onSubmit={() => applyCancelSubscription('promotion popup')}
                submitTitle="Decline & Cancel"
                cancelTitle="Back"
                warningIcon={false}
                showCloseIcon={true}
                titleLocation="center"
                transition={Transition}
            />
        );
    };

    const confirmDialog = () => {
        return (
            <Dialog
                open={isOpen}
                width="50rem"
                rootstyle={styles.dialog}
                contentstyle={styles.dialogContent}
                onClose={() => setIsOpen(false)}>
                <CloseIcon
                    src={SmallCloseIcon}
                    onClick={() => {
                        onCancelDialog('are you sure popup');
                    }}
                />
                <Title>Are you sure?</Title>
                <DialogContent>We have a few great product updates coming up and we'd love for you to stick around.</DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            onCancelDialog('are you sure popup');
                        }}
                        color="secondary"
                        sx={styles.closeButton}>
                        Back
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => applyCancelSubscription('are you sure popup')}
                        color="secondary"
                        sx={styles.submitButton}>
                        Yes, cancel
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const dialogByFlagAndPlanGroup = (cancelationFlag: cancelationProcessFlag, isSuggestDiscount: boolean) => {
        if (cancelationFlag === cancelationProcessFlag.roll_over_credits) {
            return rollOverDialog();
        }

        if (cancelationFlag === cancelationProcessFlag.control) {
            if (isSuggestDiscount) {
                return discountDialog();
            }
            return confirmDialog();
        }

        return null;
    };

    return (
        <>
            {dialogByFlagAndPlanGroup(cancelationFlag, isSuggestDiscount)}
            {LoadingModal()}
            {ConfirmationModal()}
            {CancelationModal()}
        </>
    );
};
